import React, { useState } from 'react';
import { Medal, X, Check, Calendar } from 'lucide-react';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import { useTheme } from '../../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const AddAchievementModal = ({ isOpen, onClose, onSubmit, achievement = null }) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const isEditMode = !!achievement;
  
  const [formData, setFormData] = useState({
    title: achievement?.title || '',
    date: achievement?.date ? dayjs(achievement.date) : null,
    description: achievement?.description || '',
  });

  React.useEffect(() => {
    if (achievement) {
      setFormData({
        title: achievement.title || '',
        date: achievement.date ? (typeof achievement.date === 'string' ? dayjs(achievement.date) : achievement.date) : null,
        description: achievement.description || '',
      });
    } else {
      setFormData({
        title: '',
        date: null,
        description: '',
      });
    }
  }, [achievement, isOpen]);

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      return;
    }
    onSubmit({
      ...formData,
      date: formData.date ? formData.date.format('MMMM YYYY') : '',
    });
    setFormData({
      title: '',
      date: null,
      description: '',
    });
    onClose();
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: 20,
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3,
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: {
        duration: 0.2,
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[2px]"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className={`w-full max-w-[500px] rounded-[2rem] shadow-2xl overflow-hidden relative flex flex-col ${
              isDark ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-slate-200'
            }`}
            style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header */}
        <div className={`px-8 pt-8 pb-4 flex items-start justify-between ${
          isDark ? 'border-b border-white/10' : 'border-b border-slate-100'
        }`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border ${
              isDark
                ? 'bg-amber-500/20 text-amber-400 border-amber-500/20'
                : 'bg-amber-50 text-amber-500 border-amber-100'
            }`}>
              <Medal size={24} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col text-left">
              <h2 className={`text-xl font-black tracking-tight leading-none mb-1 ${
                isDark ? '!text-white' : 'text-[#0f172a]'
              }`}>
                {isEditMode ? 'Edit Achievement' : 'Add Achievement'}
              </h2>
              <p className={`text-[9px] font-black uppercase tracking-widest leading-none ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                RECOGNIZE EXTRAORDINARY PERFORMANCE
              </p>
            </div>
          </div>
          <motion.button
            onClick={onClose}
            className={`w-10 h-10 rounded-full flex items-center justify-center border ${
              isDark
                ? 'bg-white/5 border-white/10 text-white/60 hover:!text-white hover:bg-white/10'
                : 'bg-slate-50 border-slate-100 text-slate-300 hover:text-slate-900'
            }`}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <X size={20} strokeWidth={3} />
          </motion.button>
        </div>

        {/* Form */}
        <div className={`px-8 pb-8 space-y-6 text-left ${
          isDark ? 'bg-white/5' : 'bg-white'
        }`}>
          <div className="space-y-2">
            <label className={`text-[10px] font-black uppercase tracking-widest ${
              isDark ? 'text-white/60' : 'text-slate-400'
            }`}>
              ACHIEVEMENT TITLE *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Innovation Award 2026"
              className={`w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all ${
                isDark
                  ? 'bg-black border-white/5 text-white placeholder-white/30 focus:border-blue-500'
                  : 'bg-[#f8fafc] border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'
              }`}
            />
          </div>
          <div className="space-y-2">
            <label className={`text-[10px] font-black uppercase tracking-widest ${
              isDark ? 'text-white/60' : 'text-slate-400'
            }`}>
              DATE / PERIOD
            </label>
            <ConfigProvider
              theme={{
                token: {
                  borderRadius: 16,
                  controlHeight: 48,
                  colorBgContainer: isDark ? 'black' : '#f8fafc',
                  colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                  colorText: isDark ? 'white' : '#1e293b',
                  fontSize: 14,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                },
              }}
            >
              <DatePicker
                value={formData.date}
                onChange={(date) => setFormData({ ...formData, date })}
                format="MMMM YYYY"
                picker="month"
                className="w-full"
                placeholder="e.g. February 2026"
                style={{ width: '100%' }}
              />
            </ConfigProvider>
          </div>
          <div className="space-y-2">
            <label className={`text-[10px] font-black uppercase tracking-widest ${
              isDark ? 'text-white/60' : 'text-slate-400'
            }`}>
              DESCRIPTION
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Briefly describe the impact or accomplishment..."
              rows={4}
              className={`w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all resize-none ${
                isDark
                  ? 'bg-black border-white/5 text-white placeholder-white/30 focus:border-blue-500'
                  : 'bg-[#f8fafc] border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'
              }`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={`px-8 py-6 border-t flex items-center gap-4 ${
          isDark ? 'bg-white/5 border-white/10' : 'bg-[#f8fafc]/30 border-slate-100'
        }`}>
          <motion.button
            onClick={onClose}
            className={`flex-1 py-3.5 text-[11px] font-black uppercase tracking-widest border rounded-2xl ${
              isDark
                ? 'text-slate-400 border-white/10 hover:bg-white/5 hover:text-white'
                : 'text-slate-400 border-slate-200 hover:bg-white'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            CANCEL
          </motion.button>
          <motion.button
            onClick={handleSubmit}
            className="flex-[1.5] py-3.5 bg-blue-600 !text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-lg hover:bg-blue-700 flex items-center justify-center gap-2"
            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(37, 99, 235, 0.4)" }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            {isEditMode ? 'UPDATE ACHIEVEMENT' : 'SAVE ACHIEVEMENT'}
            <Check size={16} strokeWidth={4} />
          </motion.button>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddAchievementModal;
